import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FiPieChart } from 'react-icons/fi';
import styles from './AdminWalletBadge.module.css';
import axiosClient from '../../api/axiosClient';
import { useCurrency } from '../../app/CurrencyContext';
import CurrencySelector from '../CurrencySelector/CurrencySelector';

interface Summary {
  netBalance: number;
  totalDeposited: number;
  totalWithdrawn: number;
  pendingDepositsCount: number;
  pendingWithdrawalsCount: number;
}

const AdminWalletBadge: React.FC = () => {
  const location = useLocation();
  const [summary, setSummary] = useState<Summary | null>(null);
  const [loading, setLoading] = useState(true);
  const { formatFromInr, selected, inrPerSelected } = useCurrency();

  const load = () => {
    setLoading(true);
    axiosClient.get('/admin/wallet-summary')
      .then(({ data }) => setSummary(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    window.addEventListener('wallet:refresh', load);
    return () => window.removeEventListener('wallet:refresh', load);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const pendingTotal = (summary?.pendingDepositsCount || 0) + (summary?.pendingWithdrawalsCount || 0);
  const rateNote = selected !== 'INR' && inrPerSelected ? `1 ${selected} = ₹${inrPerSelected.toFixed(2)}` : null;

  return (
    <div className={styles.group}>
      <Link
        to="/admin/payments"
        className={styles.badge}
        title={rateNote ? `Platform-wide net balance · Current rate: ${rateNote}` : 'Platform-wide net balance (approved deposits minus approved withdrawals)'}
      >
        <FiPieChart className={styles.icon} />
        <div className={styles.text}>
          <span className={styles.label}>Platform Wallet</span>
          <b className={styles.amount}>
            {loading || !summary ? '…' : formatFromInr(summary.netBalance)}
          </b>
          {rateNote && <span className={styles.rateNote}>{rateNote}</span>}
        </div>
        {!loading && pendingTotal > 0 && <span className={styles.pendingDot}>{pendingTotal}</span>}
      </Link>
      <CurrencySelector />
    </div>
  );
};

export default AdminWalletBadge;
